import React, { useState, useEffect } from 'react';
import { Individual, FamilyUnit, RelationshipType, ParentalType } from '../models/types';
import { DataService } from '../services/DataService';
import { useTranslation } from 'react-i18next';

interface RelationshipFormProps {
  individuals: Individual[];
  familyUnits: FamilyUnit[];
  onSave: () => void;
  onCancel: () => void;
  mode: 'family-unit' | 'add-child';
  selectedFamilyUnit?: FamilyUnit;
}

const RelationshipForm: React.FC<RelationshipFormProps> = ({ 
  individuals, 
  familyUnits, 
  onSave, 
  onCancel,
  mode,
  selectedFamilyUnit
}) => {
  const { t } = useTranslation();

  // Family unit form data
  const [familyUnitData, setFamilyUnitData] = useState<Omit<FamilyUnit, 'id'>>({
    partners: ['', ''] as [string, string],
    relationshipType: RelationshipType.MARRIED,
    children: []
  });

  // Child data
  const [childData, setChildData] = useState({
    childId: '',
    familyUnitId: '',
    parentalType: ParentalType.BIOLOGICAL
  });

  // Set initial values if editing
  useEffect(() => {
    if (mode === 'add-child' && selectedFamilyUnit) {
      setChildData(prev => ({
        ...prev,
        familyUnitId: selectedFamilyUnit.id
      }));
    }
  }, [mode, selectedFamilyUnit]);

  const handleFamilyUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('partner')) {
      const index = parseInt(name.split('-')[1]);
      const newPartners = [...familyUnitData.partners] as [string, string];
      newPartners[index] = value;
      
      setFamilyUnitData({
        ...familyUnitData,
        partners: newPartners
      });
    } else {
      setFamilyUnitData({
        ...familyUnitData,
        [name]: value
      });
    }
  };

  const handleChildDataChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setChildData({
      ...childData,
      [name]: value
    });
  };

  const handleFamilyUnitSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!familyUnitData.partners[0] || !familyUnitData.partners[1]) {
      alert('Both partners are required');
      return;
    }
    
    if (familyUnitData.partners[0] === familyUnitData.partners[1]) {
      alert('Partners must be different individuals');
      return;
    }
    
    // Create family unit
    DataService.addFamilyUnit(familyUnitData);
    onSave();
  };

  const handleAddChildSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!childData.childId || !childData.familyUnitId) {
      alert('Child and family unit are required');
      return;
    }
    
    // Check if child is already part of this family unit
    const familyUnit = familyUnits.find(unit => unit.id === childData.familyUnitId);
    if (familyUnit && familyUnit.children && familyUnit.children.includes(childData.childId)) {
      alert('This child is already part of this family unit');
      return;
    }
    
    // Create child relationship
    DataService.addChildRelationship({
      childId: childData.childId,
      familyUnitId: childData.familyUnitId,
      parentalType: childData.parentalType
    });
    
    onSave();
  };

  // Get available individuals (not already partners)
  const getAvailableIndividuals = (partnerIndex: number) => {
    // Exclude the individual selected as the other partner
    const excludeId = partnerIndex === 0 ? familyUnitData.partners[1] : familyUnitData.partners[0];
    return individuals.filter(ind => ind.id !== excludeId);
  };

  // Get available children (not already in the selected family unit)
  const getAvailableChildren = () => {
    if (!childData.familyUnitId) return individuals;
    
    const familyUnit = familyUnits.find(unit => unit.id === childData.familyUnitId);
    if (!familyUnit || !familyUnit.children) return individuals;
    
    return individuals.filter(ind => !familyUnit.children?.includes(ind.id));
  };

  return (
    <div className="relationship-form-container">
      {mode === 'family-unit' ? (
        <>
          <h2>{t('Create Family Unit')}</h2>
          <form onSubmit={handleFamilyUnitSubmit} className="relationship-form">
            <div className="modal-header">
              <h2>{t('Create Family Unit')}</h2>
              <button className="modal-close" type="button" onClick={onCancel}>×</button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="partner-0">{t('Partner 1*')}</label>
                <select
                  id="partner-0"
                  name="partner-0"
                  value={familyUnitData.partners[0]}
                  onChange={handleFamilyUnitChange}
                  required
                >
                  <option value="">{t('Select Partner 1')}</option>
                  {getAvailableIndividuals(0).map(individual => (
                    <option key={individual.id} value={individual.id}>
                      {individual.firstName} {individual.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="partner-1">{t('Partner 2*')}</label>
                <select
                  id="partner-1"
                  name="partner-1"
                  value={familyUnitData.partners[1]}
                  onChange={handleFamilyUnitChange}
                  required
                >
                  <option value="">{t('Select Partner 2')}</option>
                  {getAvailableIndividuals(1).map(individual => (
                    <option key={individual.id} value={individual.id}>
                      {individual.firstName} {individual.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="relationshipType">{t('Relationship Type')}</label>
                <select
                  id="relationshipType"
                  name="relationshipType"
                  value={familyUnitData.relationshipType}
                  onChange={handleFamilyUnitChange}
                >
                  {Object.values(RelationshipType).map(type => (
                    <option key={type} value={type}>
                      {t(type.charAt(0).toUpperCase() + type.slice(1))}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="marriageDate">{t('Marriage Date (Optional)')}</label>
                <input
                  type="date"
                  id="marriageDate"
                  name="marriageDate"
                  value={familyUnitData.marriageDate || ''}
                  onChange={(e) => setFamilyUnitData({
                    ...familyUnitData,
                    marriageDate: e.target.value
                  })}
                  placeholder={t('dd/mm/yyyy')}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="primary-button">{t('Create Family Unit')}</button>
              <button type="button" onClick={onCancel} className="secondary-button">{t('Cancel')}</button>
            </div>
          </form>
        </>
      ) : (
        <>
          <h2>{t('Add Child to Family')}</h2>
          <form onSubmit={handleAddChildSubmit} className="relationship-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="familyUnitId">{t('Family Unit*')}</label>
                <select
                  id="familyUnitId"
                  name="familyUnitId"
                  value={childData.familyUnitId}
                  onChange={handleChildDataChange}
                  required
                  disabled={!!selectedFamilyUnit}
                >
                  <option value="">{t('Select Family Unit')}</option>
                  {familyUnits.map(unit => {
                    const partner1 = individuals.find(ind => ind.id === unit.partners[0]);
                    const partner2 = individuals.find(ind => ind.id === unit.partners[1]);
                    return (
                      <option key={unit.id} value={unit.id}>
                        {partner1 ? `${partner1.firstName} ${partner1.lastName}` : 'Unknown'} & 
                        {partner2 ? ` ${partner2.firstName} ${partner2.lastName}` : ' Unknown'}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="childId">{t('Child*')}</label>
                <select
                  id="childId"
                  name="childId"
                  value={childData.childId}
                  onChange={handleChildDataChange}
                  required
                >
                  <option value="">{t('Select Child')}</option>
                  {getAvailableChildren().map(individual => (
                    <option key={individual.id} value={individual.id}>
                      {individual.firstName} {individual.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="parentalType">{t('Parental Type')}</label>
                <select
                  id="parentalType"
                  name="parentalType"
                  value={childData.parentalType}
                  onChange={handleChildDataChange}
                >
                  {Object.values(ParentalType).map(type => (
                    <option key={type} value={type}>
                      {t(type.charAt(0).toUpperCase() + type.slice(1))}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="primary-button">{t('Add Child')}</button>
              <button type="button" onClick={onCancel} className="secondary-button">{t('Cancel')}</button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default RelationshipForm;